import React, { useEffect, useState } from "react";
import "./LoanDetails.css";
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { TextField } from "@mui/material"; // Make sure it's imported
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { formControlStyle, inputLableStyle } from "../../../../calculatorStyle";

const calculateAPR = (emi, netLoanAmount, tenure) => {
  let lowerRate = 0,
    upperRate = 1,
    apr = 0;

  while (upperRate - lowerRate > 1e-6) {
    apr = (lowerRate + upperRate) / 2;
    const calculatedEMI =
      (netLoanAmount * apr * Math.pow(1 + apr, tenure)) /
      (Math.pow(1 + apr, tenure) - 1);

    if (calculatedEMI > emi) {
      upperRate = apr;
    } else {
      lowerRate = apr;
    }
  }

  return apr * 12 * 100; // Annualized APR
};

const calculateAmortizationSchedule = (amount, rate, tenure, startDate) => {
  const monthlyRate = rate / (12 * 100);
  let balance = amount;
  const emiAmount =
    (amount * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
    (Math.pow(1 + monthlyRate, tenure) - 1);

  const currencyFormatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  });

  const sanitizeValue = (value) => (isNaN(value) ? 0 : Math.round(value));

  const schedule = [];
  let date = dayjs(startDate);

  for (let i = 1; i <= tenure; i++) {
    const interest = balance * monthlyRate;
    const principal = emiAmount - interest;
    balance -= principal;

    schedule.push({
      month: i,
      date: date.format("MMM/YYYY").toUpperCase(),
      emi: currencyFormatter.format(sanitizeValue(emiAmount)),
      principal: currencyFormatter.format(sanitizeValue(principal)),
      interest: currencyFormatter.format(sanitizeValue(interest)),
      balance: currencyFormatter.format(sanitizeValue(balance)),
    });

    date = date.add(1, "month");
  }

  return schedule;
};

const LoanDetails = ({ onCheck, onSelect, onData }) => {
  const [addOrginationOpen, setAddOrginationOpen] = useState(false);
  const [processingFeeOption, setProcessingFeeOption] = useState("percentage");

  const getDefaultStartDate = () => {
    const currentDate = new Date();
    currentDate.setDate(1); // Set to the first day of the current month
    currentDate.setMonth(currentDate.getMonth() + 1); // Move to the next month
    return currentDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  const [monthlyIncome, setMonthlyIncome] = useState(1000000);
  const [existEmi, setExistEmi] = useState(1000);
  const [interestRate, setInterestRate] = useState(10);
  const [loanTenure, setLoanTenure] = useState(60);
  const [processingFeePercent, setProcessingFeePercent] = useState(0);
  const [fixedFee, setFixedFee] = useState(500);
  const [startDate, setStartDate] = useState(dayjs(getDefaultStartDate()));

  const handleOrigination = () => {
    setAddOrginationOpen((prev) => !prev);
  };

  onCheck(addOrginationOpen);

  // Processing Fee Calculated By .........
  const handleProcessingFeeOpion = (event) => {
    setProcessingFeeOption(event.target.value);
  };
  onSelect(processingFeeOption);

  // Loan Closure Date
  const calculateLoanClosureDate = () => {
    const start = new Date(startDate);
    start.setDate(1);
    start.setMonth(start.getMonth() + 1); // Move to the next month
    const closure = new Date(start);
    closure.setMonth(closure.getMonth() + parseInt(loanTenure));

    // Format to show MM/YYYY
    const month = String(closure.getMonth() + 1).padStart(2, "0");
    const year = closure.getFullYear();
    return `${month}/${year}`; // Format as MM/YYYY
  };

  // currency formate
  const currencyFormatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  });

  const sanitizeValue = (value) => (isNaN(value) ? 0 : value);

  const calculateLoanEligibility = (
    income, // Monthly income
    existingEMIs, // Total existing EMIs
    loanTenure, // Loan tenure in months
    annualInterestRate // Annual interest rate (e.g., 10 for 10%)
  ) => {
    // Constants
    const dtiRatio = 0.5; // Example DTI Ratio (50%)

    // Step 1: Calculate the affordable EMI
    const affordableEMI = income * dtiRatio - existingEMIs;

    // If affordable EMI is negative, the loan eligibility is 0
    if (affordableEMI <= 0) return 0;

    // Step 2: Calculate the loan eligibility using the formula for EMI
    const monthlyRate = annualInterestRate / 12 / 100;
    const n = loanTenure;

    // EMI calculation formula: EMI = P * r * (1+r)^n / ((1+r)^n - 1)
    // Rearranged to find the principal (loan eligibility):
    // P = EMI * ((1+r)^n - 1) / (r * (1+r)^n)
    const loanEligibility =
      (affordableEMI * (Math.pow(1 + monthlyRate, n) - 1)) /
      (monthlyRate * Math.pow(1 + monthlyRate, n));

    // Limit loan eligibility to ₹5,000,000 directly
    return isNaN(loanEligibility)
      ? 0
      : Math.round(Math.min(loanEligibility, 5000000));
  };

  // Function to calculate loan eligibility and other values
  const calculateLoan = () => {
    if (addOrginationOpen === false) {
      setFixedFee(0);
      setProcessingFeePercent(0);
    }

    const loanEligibility = calculateLoanEligibility(
      monthlyIncome,
      existEmi,
      loanTenure,
      interestRate
    );
    const r = interestRate / 12 / 100;
    const n = loanTenure;

    const processingFee =
      processingFeeOption === "percentage"
        ? sanitizeValue(
            Math.round((loanEligibility * processingFeePercent) / 100)
          )
        : sanitizeValue(Math.round(fixedFee || 0));

    const adjustedLoanEligibility = sanitizeValue(
      loanEligibility - processingFee
    );

    const emi =
      r === 0
        ? Math.round(adjustedLoanEligibility / loanTenure) // Adjusted loan eligibility for EMI calculation
        : Math.round(
            (adjustedLoanEligibility * r * (1 + r) ** n) / ((1 + r) ** n - 1)
          );

    const principal = sanitizeValue(
      r === 0
        ? adjustedLoanEligibility
        : Math.round((emi * ((1 + r) ** n - 1)) / (r * (1 + r) ** n))
    );

    const netLoanAmount = loanEligibility - processingFee;
    const totalInterest = sanitizeValue(Math.round(emi * n - principal));
    const totalLoanPayments = sanitizeValue(
      Math.round(principal + totalInterest)
    );

    let apr = calculateAPR(emi, netLoanAmount, loanTenure);
    apr = isNaN(apr) ? 0 : Math.round(apr);

    const schedule = calculateAmortizationSchedule(
      adjustedLoanEligibility,
      interestRate,
      loanTenure,
      startDate
    );
    // Prepare results

    const formData = {
      loanEligibility: currencyFormatter.format(loanEligibility),
      adjustedLoanEligibility: currencyFormatter.format(
        adjustedLoanEligibility
      ),

      newLoanEligibility:
        processingFee > 0
          ? currencyFormatter.format(adjustedLoanEligibility)
          : currencyFormatter.format(loanEligibility),
      totalPrincipal: currencyFormatter.format(principal),
      emi: currencyFormatter.format(emi),
      totalInterest: currencyFormatter.format(totalInterest),
      totalLoanPayments: currencyFormatter.format(totalLoanPayments),
      processingFee: currencyFormatter.format(processingFee),
      loanClosureDate: calculateLoanClosureDate(),
      apr: apr,
      amortizationSchedule: schedule,
    };

    onData(formData); // Send results to parent component
  };

  useEffect(() => {
    calculateLoan(); // Trigger calculation when component is mounted or state changes
  }, [
    monthlyIncome,
    existEmi,
    interestRate,
    addOrginationOpen,
    loanTenure,
    processingFeePercent,
    fixedFee,
    processingFeeOption,
    startDate,
  ]);

  const formatCurrency = (value) => {
    return value.toLocaleString("en-IN");
  };

  const handleAmountChange = (e, setter) => {
    const value = e.target.value.replace(/,/g, ""); // Remove commas
    if (/^\d*\.?\d*$/.test(value)) {
      const numericValue = Number(value); // Convert to number
      if (numericValue <= 100000000) {
        // Restrict to maximum 50,00,000
        setter(numericValue);
      }
    }
  };

  return (
    <>
      <div
        id="loan_details__container"
        className=" loan_details__content__box rounded py-4 px-3"
      >
        <Typography
          sx={{
            fontSize: "16px",
            lineHeight: "24px",

            fontFamily: "'Archivo', sans-serif",
          }}
          className="px-2 fw-bold"
        >
          Loan details
        </Typography>
        <div className="d-flex flex-column gap-1 mt-3">
          <div className="details_box p-2">
            <p style={inputLableStyle} className=" mb-1">
              Monthly Income (₹){" "}
              <span style={{ color: "#8A8A8A" }}>(required)</span>
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <span className="archivo">&#8377;</span>
                  </InputAdornment>
                }
                value={formatCurrency(monthlyIncome)} // Display value with commas
                onChange={(e) => handleAmountChange(e, setMonthlyIncome)}
              />
            </FormControl>
          </div>
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Existing EMIs/Debts (₹)
              <span style={{ color: "#8A8A8A" }}>(required)</span>
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <span className="archivo">&#8377;</span>
                  </InputAdornment>
                }
                value={formatCurrency(existEmi)} // Display value with commas
                onChange={(e) => handleAmountChange(e, setExistEmi)}
              />
            </FormControl>
          </div>
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Interest rate
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                sx={{
                  fontFamily: "'Archivo',sans-serif",
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <span className="archivo">%</span>
                  </InputAdornment>
                }
                value={interestRate || 0}
                onChange={(e) => {
                  let value = e.target.value.replace(/,/g, ""); // Remove commas

                  if (/^\d*\.?\d*$/.test(value)) {
                    if (value === "") {
                      setInterestRate(""); // Allow empty field temporarily
                    } else {
                      setInterestRate(value.replace(/^0+/, "") || "0"); // Remove leading zero
                    }
                  }
                }}
                onFocus={() => interestRate === "0" && setInterestRate("")} // Empty field on focus
                onBlur={() =>
                  setInterestRate(interestRate === "" ? "0" : interestRate)
                }
              />
            </FormControl>
          </div>
          <div className="details_box  p-2">
            <p style={inputLableStyle} className="mb-1">
              Loan term (months)
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                value={loanTenure}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!isNaN(value) && Number.isInteger(+value) && value >= 0) {
                    setLoanTenure(value);
                  }
                }}
              />
            </FormControl>
          </div>
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Start date
            </p>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker   renderInput={(params) => <TextField {...params} size="small" />}

                views={["month", "year"]}
                format="MM/YYYY"
                name="startDate"
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    height: "40px",
                    border: "1.5px solid #b783cc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 10px",
                    border: "none",
                  },
                }}
                value={startDate}
                onChange={(newDate) =>
                  setStartDate(newDate ? dayjs(newDate) : null)
                }
              />
            </LocalizationProvider>
          </div>
          <div className="px-2 mt-3">
            <Typography
              className="archivo"
              sx={{
                fontSize: "16px",
                lineHeight: "24px",
                fontWeight: 700,
                color: "#59266D",
                fontFamily: "'Archivo', sans-serif",
              }}
              onClick={handleOrigination}
            >
              Add Processing Fee &nbsp;
              <span>
                {" "}
                <FontAwesomeIcon
                  icon={addOrginationOpen ? faChevronUp : faChevronDown}
                  style={{ fontSize: 16 }}
                />{" "}
              </span>
            </Typography>
            <div
              className={`origination_details ${
                addOrginationOpen === true ? "d-flex" : "d-none"
              }  flex-column gap-1 mt-4`}
            >
              <div className="details_box py-2">
                <p className="loan_details__label archivo mb-1">
                  How are processing fees calculated?
                </p>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="radio-buttons-group"
                  className="gap-2"
                  value={processingFeeOption}
                  onChange={handleProcessingFeeOpion}
                >
                  <div
                    style={{
                      width: "100%",
                      border: "1.5px solid #b783cc",
                      padding: "0.1px 2px 0.1px 0",
                      backgroundColor: "#fff",
                      borderRadius: "4px",
                    }}
                  >
                    <FormControlLabel
                      value="percentage"
                      sx={{ paddingLeft: "13px" }}
                      control={<Radio size="small" />}
                      label={
                        <span className="radio_label text-break archivo">
                          Percentage
                        </span>
                      }
                    />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      border: "1.5px solid #b783cc",
                      padding: "0.1px 2px 0.1px 0",
                      backgroundColor: "#fff",
                      borderRadius: "4px",
                    }}
                  >
                    <FormControlLabel
                      value="fixedAmount"
                      sx={{ paddingLeft: "13px" }}
                      control={<Radio size="small" />}
                      label={
                        <span className="radio_label text-break archivo">
                          Fixed amount
                        </span>
                      }
                    />
                  </div>
                </RadioGroup>
              </div>

              <div className="details_box  py-2">
                <p className="loan_details__label archivo mb-1">
                  Processing fee
                  <span style={{ color: "#8A8A8A" }}>(optional)</span>
                </p>
                <FormControl fullWidth sx={formControlStyle}>
                  {processingFeeOption === "fixedAmount" ? (
                    <OutlinedInput
                      id="outlined-adornment-amount"
                      fullWidth
                      size="small"
                      startAdornment={
                        <InputAdornment position="start">
                          <span className="archivo">&#8377;</span>
                        </InputAdornment>
                      }
                      value={formatCurrency(fixedFee)} // Display value with commas
                      onChange={(e) => handleAmountChange(e, setFixedFee)}
                    />
                  ) : (
                    <OutlinedInput
                      id="outlined-adornment-amount"
                      fullWidth
                      size="small"
                      endAdornment={
                        <InputAdornment position="end">
                          <span className="archivo">%</span>
                        </InputAdornment>
                      }
                      value={processingFeePercent || 0}
                      onChange={(e) => {
                        let value = e.target.value.replace(/,/g, ""); // Remove commas

                        if (/^\d*\.?\d*$/.test(value)) {
                          if (value === "") {
                            setProcessingFeePercent(""); // Allow empty field temporarily
                          } else {
                            setProcessingFeePercent(
                              value.replace(/^0+/, "") || "0"
                            ); // Remove leading zero
                          }
                        }
                      }}
                      onFocus={() =>
                        processingFeePercent === "0" &&
                        setProcessingFeePercent("")
                      } // Empty field on focus
                      onBlur={() =>
                        setProcessingFeePercent(
                          processingFeePercent === ""
                            ? "0"
                            : processingFeePercent
                        )
                      }
                    />
                  )}
                </FormControl>
              </div>

              {/* <div className="details_box mt-2">
                <p className="loan_details__label archivo mb-1">
                  Credit Score{" "}
                  <span style={{ color: "#8A8A8A" }}>(optional)</span>
                </p>
                <FormControl fullWidth sx={formControlStyle}>
                  <OutlinedInput
                    id="outlined-adornment-amount"
                    fullWidth
                    size="small"
                    // startAdornment={
                    //   <InputAdornment position="start">
                    //     <span className="archivo">&#8377;</span>
                    //   </InputAdornment>
                    // }
                    value={creditScore} // Display value with commas
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!isNaN(value) && value >= 0) {
                        setCreditScore(value);
                      }
                    }}
                  />
                </FormControl>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanDetails;
